import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface Task {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  taskType: string;
  clientId: string | null;
  clientName: string | null;
  caseId: string | null;
  caseTitle: string | null;
  leadId: string | null;
  leadName: string | null;
  legalProductId: string | null;
  legalProductName: string | null;
  responsibleUser: string | null;
  dueDate: string | null;
  completedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskInput {
  title: string;
  description?: string | null;
  status?: string;
  priority?: string;
  taskType?: string;
  clientId?: string | null;
  caseId?: string | null;
  leadId?: string | null;
  legalProductId?: string | null;
  responsibleUser?: string | null;
  dueDate?: string | null;
  completedAt?: string | null;
  notes?: string | null;
}

export type UpdateTaskInput = Partial<CreateTaskInput>;

export interface TaskLoadFilters {
  status?: string;
  clientId?: string;
  caseId?: string;
  leadId?: string;
}

type UseTasksOptions = {
  autoLoad?: boolean;
};

const allowedStatuses = new Set(['pending', 'in_progress', 'completed', 'cancelled', 'archived']);
const allowedPriorities = new Set(['low', 'normal', 'high', 'urgent']);
const allowedTaskTypes = new Set([
  'general',
  'deadline',
  'hearing',
  'document',
  'follow_up',
  'financial',
  'client_contact',
  'internal',
]);

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toTaskStatus = (value: unknown) => {
  const text = toNullableText(value);
  if (!text) {
    return 'pending';
  }

  const normalized = text.toLowerCase();
  return allowedStatuses.has(normalized) ? normalized : 'pending';
};

const toTaskPriority = (value: unknown) => {
  const text = toNullableText(value);
  if (!text) {
    return 'normal';
  }

  const normalized = text.toLowerCase();
  return allowedPriorities.has(normalized) ? normalized : 'normal';
};

const toTaskType = (value: unknown) => {
  const text = toNullableText(value);
  if (!text) {
    return 'general';
  }

  const normalized = text.toLowerCase();
  return allowedTaskTypes.has(normalized) ? normalized : 'general';
};

const parseTimestamp = (value: string | null) => {
  if (!value) {
    return Number.POSITIVE_INFINITY;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const sortTasks = (a: Task, b: Task) => {
  const aDue = parseTimestamp(a.dueDate);
  const bDue = parseTimestamp(b.dueDate);

  if (aDue !== bDue) {
    return aDue - bDue;
  }

  const aCreated = Date.parse(a.createdAt);
  const bCreated = Date.parse(b.createdAt);

  if (Number.isFinite(aCreated) && Number.isFinite(bCreated)) {
    return bCreated - aCreated;
  }

  return b.id.localeCompare(a.id);
};

const normalizeTask = (value: any): Task => ({
  id: String(value?.id ?? ''),
  title: String(value?.title ?? '').trim(),
  description: toNullableText(value?.description ?? value?.description),
  status: toTaskStatus(value?.status),
  priority: toTaskPriority(value?.priority),
  taskType: toTaskType(value?.taskType ?? value?.task_type),
  clientId: toNullableText(value?.clientId ?? value?.client_id),
  clientName: toNullableText(value?.clientName ?? value?.client_name),
  caseId: toNullableText(value?.caseId ?? value?.case_id),
  caseTitle: toNullableText(value?.caseTitle ?? value?.case_title),
  leadId: toNullableText(value?.leadId ?? value?.lead_id),
  leadName: toNullableText(value?.leadName ?? value?.lead_name),
  legalProductId: toNullableText(value?.legalProductId ?? value?.legal_product_id),
  legalProductName: toNullableText(value?.legalProductName ?? value?.legal_product_name),
  responsibleUser: toNullableText(value?.responsibleUser ?? value?.responsible_user),
  dueDate: toNullableText(value?.dueDate ?? value?.due_date),
  completedAt: toNullableText(value?.completedAt ?? value?.completed_at),
  notes: toNullableText(value?.notes),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

export function useTasks(options: UseTasksOptions = {}) {
  const { autoLoad = true } = options;
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadTasks = useCallback(async (filters: TaskLoadFilters = {}) => {
    const requestId = ++requestIdRef.current;
    setIsLoadingTasks(true);
    setTasksError(null);

    try {
      const searchParams = new URLSearchParams();

      if (filters.status?.trim()) {
        searchParams.set('status', filters.status.trim());
      }

      if (filters.clientId?.trim()) {
        searchParams.set('clientId', filters.clientId.trim());
      }

      if (filters.caseId?.trim()) {
        searchParams.set('caseId', filters.caseId.trim());
      }

      if (filters.leadId?.trim()) {
        searchParams.set('leadId', filters.leadId.trim());
      }

      const endpoint = searchParams.toString() ? `/api/tasks?${searchParams.toString()}` : '/api/tasks';
      const response = await fetchWithRetry(endpoint);
      const data = await response.json();

      if (!response.ok || !data.success || !Array.isArray(data.tasks)) {
        throw new Error(data.error || `GET /api/tasks falhou com status ${response.status}`);
      }

      if (requestId === requestIdRef.current) {
        setTasks(data.tasks.map(normalizeTask).sort(sortTasks));
      }
    } catch (error) {
      console.error('Erro ao carregar tarefas reais. ', error);

      if (requestId === requestIdRef.current) {
        setTasks([]);
        setTasksError('Não foi possível carregar os dados reais. Tente novamente.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingTasks(false);
      }
    }
  }, []);

  const createTask = useCallback(async (input: CreateTaskInput) => {
    const response = await fetch('/api/tasks', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.task) {
      throw new Error(data.error || `POST /api/tasks falhou com status ${response.status}`);
    }

    const created = normalizeTask(data.task);
    setTasks((prev) => [created, ...prev.filter((task) => task.id !== created.id)].sort(sortTasks));
    return created;
  }, []);

  const updateTask = useCallback(async (id: string, input: UpdateTaskInput) => {
    const response = await fetch(`/api/tasks/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.task) {
      throw new Error(data.error || `PUT /api/tasks/${id} falhou com status ${response.status}`);
    }

    const updated = normalizeTask(data.task);
    setTasks((prev) => [updated, ...prev.filter((task) => task.id !== updated.id)].sort(sortTasks));
    return updated;
  }, []);

  const completeTask = useCallback(
    async (id: string) => {
      return updateTask(id, {
        status: 'completed',
      });
    },
    [updateTask]
  );

  const deleteTask = useCallback(async (id: string) => {
    const response = await fetch(`/api/tasks/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `DELETE /api/tasks/${id} falhou com status ${response.status}`);
    }

    setTasks((prev) =>
      prev
        .map((task) =>
          task.id === id
            ? {
                ...task,
                status: 'archived',
                updatedAt: new Date().toISOString(),
              }
            : task
        )
        .sort(sortTasks)
    );

    return true;
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void loadTasks();
  }, [autoLoad, loadTasks]);

  return {
    tasks,
    isLoadingTasks,
    tasksError,
    loadTasks,
    createTask,
    updateTask,
    completeTask,
    deleteTask,
  };
}
